import React, { useState, useEffect, useRef } from 'react';
import { Upload, Calendar, X, CheckCircle, AlertCircle, Eye } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { blotterService } from '../../services/blotter';
import { accountService } from '../../services';
import { getPreviousWorkingDay } from '../../utils/dateUtils';
import { NewAccountReviewModal } from './NewAccountReviewModal';
import type {
  AccountAssignment,
  ExistingAccountAssignment,
  MatchedAccountNeedingEmployees,
  ReviewModalInitialState,
} from './NewAccountReviewModal';
import type { ExistingAssignmentMap } from './useReviewModalAssignments';

interface BlotterUploadProps {
  onUploadSuccess?: () => void;
}

interface UploadFile {
  id: string;
  file: File;
  date: string;
  status: 'pending' | 'uploading' | 'uploaded' | 'processing' | 'completed' | 'error';
  error?: string;
  blotterId?: number;
  processingStatus?: 'pending' | 'processing' | 'completed' | 'error';
}

interface ReviewModalState {
  isOpen: boolean;
  fileIndex: number;
  newAccounts: any[];
  matchedAccountsNeedingEmployees: MatchedAccountNeedingEmployees[];
  filename: string;
  totalTransactions: number;
  totalCommission: number;
}

const BlotterUpload: React.FC<BlotterUploadProps> = ({ onUploadSuccess }) => {
  const [files, setFiles] = useState<UploadFile[]>([]);
  const [processImmediately, setProcessImmediately] = useState(true);
  const [isUploading, setIsUploading] = useState(false);
  const [isDragging, setIsDragging] = useState(false);
  const [isPreviewing, setIsPreviewing] = useState(false);
  const [reviewModal, setReviewModal] = useState<ReviewModalState>({
    isOpen: false,
    fileIndex: -1,
    newAccounts: [],
    matchedAccountsNeedingEmployees: [],
    filename: '',
    totalTransactions: 0,
    totalCommission: 0,
  });
  // R15 Task B1: last-submitted modal state, kept across a 422 retry so
  // when the review modal reopens for the same file the user's prior
  // assignments + house-override ticks are restored. Cleared on cancel,
  // successful upload, or when a different file enters the review flow.
  const [persistedReview, setPersistedReview] = useState<ReviewModalInitialState | null>(null);
  const navigate = useNavigate();
  const pollingIntervalRef = useRef<NodeJS.Timeout | null>(null);
  // Queue of files to process after preview
  const uploadQueueRef = useRef<{ file: UploadFile; assignments: Record<string, any> }[]>([]);

  const addFiles = (selectedFiles: File[]) => {
    const validExtensions = ['.csv', '.xls', '.xlsx'];
    const validFiles = selectedFiles.filter(file => {
      const ext = file.name.toLowerCase().substring(file.name.lastIndexOf('.'));
      return validExtensions.includes(ext);
    });

    const newFiles: UploadFile[] = validFiles.map(file => ({
      id: `${file.name}-${Date.now()}-${Math.random()}`,
      file,
      date: getPreviousWorkingDay(),
      status: 'pending'
    }));

    setFiles(prev => [...prev, ...newFiles]);
  };

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const selectedFiles = Array.from(e.target.files || []);
    addFiles(selectedFiles);
  };

  const handleDragEnter = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(true);
  };

  const handleDragLeave = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);

    const droppedFiles = Array.from(e.dataTransfer.files);
    addFiles(droppedFiles);
  };

  const handleDateChange = (id: string, date: string) => {
    setFiles(prev => prev.map(f => f.id === id ? { ...f, date } : f));
  };

  const handleRemoveFile = (id: string) => {
    setFiles(prev => prev.filter(f => f.id !== id));
  };

  // Poll blotter status for files that are processing
  useEffect(() => {
    const processingFiles = files.filter(
      f => f.blotterId && (f.status === 'uploaded' || f.status === 'processing')
    );

    if (processingFiles.length === 0) {
      if (pollingIntervalRef.current) {
        clearInterval(pollingIntervalRef.current);
        pollingIntervalRef.current = null;
      }
      return;
    }

    const pollStatus = async () => {
      let hasNewlyCompleted = false;

      for (const file of processingFiles) {
        if (!file.blotterId) continue;

        try {
          const response = await blotterService.getBlotter(file.blotterId);
          const blotter = response.data;

          if (!blotter) continue;

          setFiles(prev => prev.map(f => {
            if (f.id !== file.id) return f;

            if (blotter.status === 'completed' && f.status !== 'completed') {
              hasNewlyCompleted = true;
              return {
                ...f,
                status: 'completed',
                processingStatus: 'completed'
              };
            } else if (blotter.status === 'error') {
              return {
                ...f,
                status: 'error',
                processingStatus: 'error',
                error: blotter.error_message || 'Processing failed'
              };
            } else if (blotter.status === 'processing') {
              return {
                ...f,
                status: 'processing',
                processingStatus: 'processing'
              };
            }

            return f;
          }));
        } catch (error) {
          console.error('Failed to poll blotter status:', error);
        }
      }

      // Trigger refresh callback when a file completes processing
      if (hasNewlyCompleted && onUploadSuccess) {
        onUploadSuccess();
      }
    };

    // Poll immediately then every 2 seconds
    pollStatus();
    pollingIntervalRef.current = setInterval(pollStatus, 2000);

    return () => {
      if (pollingIntervalRef.current) {
        clearInterval(pollingIntervalRef.current);
      }
    };
  }, [files, onUploadSuccess]);

  // Cleanup interval on unmount
  useEffect(() => {
    return () => {
      if (pollingIntervalRef.current) {
        clearInterval(pollingIntervalRef.current);
      }
    };
  }, []);

  /**
   * Result of a single upload attempt. `retry` distinguishes recoverable
   * 422 "unresolved accounts" errors (file is reset to pending so the
   * user can reopen the review modal) from hard errors (file goes to the
   * error status permanently).
   */
  interface UploadResult {
    blotterId: number | null;
    retry: boolean;
  }

  /**
   * Upload a single file and return the new blotter ID, or null on failure.
   * Always leaves the file in 'uploaded' state — 'completed' is only set by
   * the polling effect when the backend confirms processing finished.
   *
   * `houseOverrides` carries the per-account opt-ins from the review modal
   * through to the server's `house_overrides[]` validation. Empty by default
   * because most uploads have zero employee-less matched accounts.
   *
   * Return shape uses `UploadResult` so callers can tell the difference
   * between a hard failure and a retryable 422 (see catch block).
   */
  const uploadSingleFile = async (
    uploadFile: UploadFile,
    shouldProcess: boolean = true,
    houseOverrides: number[] = []
  ): Promise<UploadResult> => {
    setFiles(prev => prev.map(f =>
      f.id === uploadFile.id ? { ...f, status: 'uploading' } : f
    ));

    try {
      const response = await blotterService.uploadBlotter(
        uploadFile.file,
        uploadFile.date,
        shouldProcess,
        houseOverrides
      );

      const blotterId = response.data?.id;

      setFiles(prev => prev.map(f =>
        f.id === uploadFile.id
          ? { ...f, status: 'uploaded', blotterId, processingStatus: 'pending' }
          : f
      ));

      return { blotterId: blotterId ?? null, retry: false };
    } catch (error: any) {
      // R13 Task C-r: the upload endpoint returns 422 with
      // { error, unresolved_accounts: number[] } when the caller forgot
      // to opt unassigned accounts into house_overrides.
      const data = error.response?.data;
      const unresolved: number[] | undefined = Array.isArray(data?.unresolved_accounts)
        ? data.unresolved_accounts
        : undefined;

      // R13 CR HIGH #2: a 422 with unresolved_accounts is RECOVERABLE —
      // the file only needs the user to re-open the review modal and
      // resolve the flagged accounts. Previously we flipped it to
      // status='error', which then made the next "Upload" click filter
      // it out (only pending files get uploaded), stranding the file
      // until the user removed & re-added it. Instead: reset to pending
      // with an informative message and let the caller re-invoke the
      // preview flow so the modal pops with the unresolved accounts
      // highlighted.
      if (error.response?.status === 422 && unresolved && unresolved.length > 0) {
        const errorMessage =
          `${unresolved.length} account${unresolved.length !== 1 ? 's' : ''} still need to be resolved. Reopen the review to assign employees or send to House.`;

        setFiles(prev => prev.map(f =>
          f.id === uploadFile.id
            ? { ...f, status: 'pending', error: errorMessage }
            : f
        ));

        return { blotterId: null, retry: true };
      }

      const errorMessage = data?.message || data?.error || error.message || 'Upload failed';
      setFiles(prev => prev.map(f =>
        f.id === uploadFile.id
          ? { ...f, status: 'error', error: errorMessage }
          : f
      ));

      return { blotterId: null, retry: false };
    }
  };

  const handleUpload = async () => {
    if (files.length === 0) return;

    const pendingFiles = files.filter(f => f.status === 'pending');
    if (pendingFiles.length === 0) return;

    setIsPreviewing(true);

    // Preview each pending file to check for new accounts AND for existing
    // accounts that currently have no employees. The latter case would
    // silently route 100% of commission to House if we proceeded without
    // intervention — the preview API already returns has_employees per
    // matched account, so we surface those rows in the review modal too.
    for (let i = 0; i < pendingFiles.length; i++) {
      const uploadFile = pendingFiles[i];
      try {
        const result = await blotterService.previewBlotter(uploadFile.file);
        const newAccounts = result.data?.new_accounts || [];
        const matched = result.data?.matched_accounts || [];
        const employeeLess: MatchedAccountNeedingEmployees[] = matched
          .filter((a: any) => a.has_employees === false)
          .map((a: any) => ({
            account_id: a.account_id,
            account_name: a.account_name,
            client_name: a.client_name,
            client_id: a.client_id,
            account_string: a.account_string,
            has_employees: false,
          }));

        // Open review modal when EITHER:
        //   (1) there are new (unmatched) accounts the user must assign, OR
        //   (2) there are existing accounts with zero employees that would
        //       silently send 100% to House if we proceeded.
        if (newAccounts.length > 0 || employeeLess.length > 0) {
          setIsPreviewing(false);
          uploadQueueRef.current = pendingFiles.slice(i).map(f => ({ file: f, assignments: {} }));
          setReviewModal({
            isOpen: true,
            fileIndex: i,
            newAccounts,
            matchedAccountsNeedingEmployees: employeeLess,
            filename: uploadFile.file.name,
            totalTransactions: result.data?.total_transactions || 0,
            totalCommission: result.data?.total_commission || 0,
          });
          return; // Stop here — modal callbacks will continue the upload
        }
      } catch (error: any) {
        console.error('Preview failed, proceeding with upload:', error.message);
        // If preview fails, proceed with upload anyway
      }
    }

    // No new accounts found in any file — upload all directly
    setIsPreviewing(false);
    setIsUploading(true);

    for (const uploadFile of pendingFiles) {
      await uploadSingleFile(uploadFile, processImmediately);
    }

    // Note: any 422 unresolved_accounts responses inside the loop reset
    // the file to 'pending' with an informative error. We don't re-invoke
    // preview here — the user can just click Upload again to retry.

    setIsUploading(false);

    if (!processImmediately && onUploadSuccess) {
      onUploadSuccess();
      setTimeout(() => setFiles([]), 2000);
    }
  };

  /**
   * Apply the modal's assignments to the previewed file and trigger processing.
   *
   * Two separate downstream APIs:
   *   - NEW accounts → blotterService.applyAssignments (creates the account
   *     records during processing).
   *   - EXISTING-but-empty accounts → accountService.bulkAssignEmployees per
   *     account, with overwriteExisting=false (we ADD employees, not replace).
   *     A Promise.all loop is required because bulkAssignEmployees broadcasts
   *     ONE employee set across MANY accounts — wrong shape for our
   *     per-account-different assignments. Lack of atomicity is acceptable
   *     for the typical ≤ 5 empty accounts per upload; a partial failure
   *     leaves any succeeded assignments in place and surfaces the error.
   *
   * The assignments only correspond to the FIRST file that was previewed —
   * remaining queued files (if any) restart the preview/review loop.
   */
  const handleReviewProceed = async (
    newAssignments: Record<string, AccountAssignment>,
    existingAssignments: ExistingAccountAssignment[],
    /**
     * R13 Task C-r: account ids the user explicitly ticked "Send to House"
     * for. Forwarded to the upload call's `house_overrides[]` so the server
     * persists a House UserAccount before processing begins. Without these
     * the upload would be rejected with 422 `unresolved_accounts`.
     */
    houseOverrideAccountIds: number[]
  ) => {
    setReviewModal(prev => ({ ...prev, isOpen: false }));
    setIsUploading(true);

    // R15 Task B1: snapshot the user's work BEFORE we call the upload API.
    // If the upload comes back 422 unresolved_accounts, the modal is
    // re-opened via the preview flow and this snapshot is passed back in
    // as `initialState` so the user does not have to re-enter everything.
    const existingSnapshot: ExistingAssignmentMap = {};
    existingAssignments.forEach((a) => {
      existingSnapshot[a.account_id] = { user_assignments: a.user_assignments };
    });
    setPersistedReview({
      newAssignments,
      existingAssignments: existingSnapshot,
      houseOverrides: houseOverrideAccountIds,
    });

    const accountsPayload = Object.entries(newAssignments).map(([accountString, val]) => ({
      account_string: accountString,
      client_id: val.client_id ?? null,
      user_assignments: val.user_assignments,
    }));

    // First in queue is the file the assignments belong to.
    const previewed = uploadQueueRef.current[0];
    const remaining = uploadQueueRef.current.slice(1);
    uploadQueueRef.current = [];

    if (previewed) {
      // 1) Push employee assignments to existing accounts FIRST so that when
      //    ProcessBlotterJob runs (triggered by applyAssignments below) it
      //    sees the freshly-assigned employees instead of routing to House.
      try {
        await Promise.all(
          existingAssignments.map((a) =>
            accountService.bulkAssignEmployees(
              [a.account_id],
              a.user_assignments,
              /* overwriteExisting */ false
            )
          )
        );
      } catch (error: any) {
        const errorMessage =
          error.response?.data?.message ||
          error.response?.data?.error ||
          error.message ||
          'Failed to assign employees to existing accounts';
        setFiles(prev => prev.map(f =>
          f.id === previewed.file.id
            ? { ...f, status: 'error', error: errorMessage }
            : f
        ));
        setIsUploading(false);
        return;
      }

      // 2) Upload the file (without auto-processing) and apply the
      //    new-account assignments — same path as before. House overrides
      //    are forwarded here so the backend persists House UserAccount
      //    rows for opted-in accounts before the processing job runs.
      const uploadResult = await uploadSingleFile(previewed.file, false, houseOverrideAccountIds);

      // R13 CR HIGH #2: 422 with unresolved_accounts means the user
      // missed a required assignment. The file has been reset to
      // 'pending' by uploadSingleFile; re-trigger the preview flow so
      // the review modal re-opens with the same file and the unresolved
      // accounts highlighted. Any remaining queued files will follow.
      if (uploadResult.retry) {
        setIsUploading(false);
        await handleUpload();
        return;
      }

      const blotterId = uploadResult.blotterId;
      if (blotterId) {
        try {
          // Respect the global "Process files immediately" checkbox: if user opted out,
          // assignments are saved but processing must be triggered manually later.
          await blotterService.applyAssignments(blotterId, {
            auto_process: processImmediately,
            accounts: accountsPayload,
          });

          // If user opted out of auto-processing, clear processingStatus so polling
          // doesn't keep checking — file is in "uploaded, awaiting manual process".
          if (!processImmediately) {
            setFiles(prev => prev.map(f =>
              f.id === previewed.file.id
                ? { ...f, processingStatus: undefined, status: 'completed' }
                : f
            ));
          }
          // R15 Task B1: upload path succeeded end-to-end — discard the
          // retry snapshot so a later, unrelated review does not rehydrate
          // stale assignments.
          setPersistedReview(null);
        } catch (error: any) {
          const errorMessage =
            error.response?.data?.message ||
            error.response?.data?.error ||
            error.message ||
            'Failed to apply assignments';

          setFiles(prev => prev.map(f =>
            f.id === previewed.file.id
              ? { ...f, status: 'error', error: errorMessage }
              : f
          ));
          // Non-retry failure — don't hold onto the snapshot indefinitely.
          setPersistedReview(null);
        }
      }
    }

    setIsUploading(false);

    // Restart preview flow for any remaining files (each gets its own review modal).
    if (remaining.length > 0) {
      // Re-trigger handleUpload for the remaining pending files via the existing logic.
      await handleUpload();
    }
  };

  /**
   * User skipped assignment: upload as-is. ProcessBlotterJob will auto-create
   * accounts under "Unassigned" client (only if processImmediately is set).
   * Skips ONLY the previewed file; remaining queued files re-enter the review flow.
   */
  const handleReviewSkip = async () => {
    setReviewModal(prev => ({ ...prev, isOpen: false }));
    setIsUploading(true);
    // R15 Task B1: user chose to skip assignment entirely — drop any
    // rehydration snapshot so a later review starts clean.
    setPersistedReview(null);

    const previewed = uploadQueueRef.current[0];
    const remaining = uploadQueueRef.current.slice(1);
    uploadQueueRef.current = [];

    if (previewed) {
      // Respect the user's "Process files immediately" checkbox.
      const skipResult = await uploadSingleFile(previewed.file, processImmediately);
      // If the skip path also trips a 422 (e.g. matched accounts with no
      // employees but user skipped assignment), the file is now pending
      // again — re-enter the preview flow so the modal re-opens.
      if (skipResult.retry) {
        setIsUploading(false);
        await handleUpload();
        return;
      }
    }

    setIsUploading(false);

    if (remaining.length > 0) {
      await handleUpload();
    }
  };

  const handleViewBlotter = (uploadFile: UploadFile) => {
    // Navigate to blotter report with the date
    navigate(`/reports/blotter?date=${uploadFile.date.split('T')[0]}`);
  };

  const totalFiles = files.length;
  const uploadedCount = files.filter(f => f.status === 'uploaded' || f.status === 'processing' || f.status === 'completed').length;
  const completedCount = files.filter(f => f.status === 'completed').length;
  const processingCount = files.filter(f => f.status === 'processing' || f.status === 'uploaded').length;
  const errorCount = files.filter(f => f.status === 'error').length;

  return (
    <div className="bg-white rounded-lg shadow p-6">
      <h2 className="text-2xl font-semibold mb-6">Upload Daily Blotter</h2>

      {/* File Upload Area */}
      <div className="mb-6">
        <label className="block w-full">
          <div
            onDragEnter={handleDragEnter}
            onDragLeave={handleDragLeave}
            onDragOver={handleDragOver}
            onDrop={handleDrop}
            className={`border-2 border-dashed rounded-lg p-8 text-center cursor-pointer transition-all ${
              isDragging
                ? 'border-blue-500 bg-blue-50'
                : 'border-gray-300 hover:border-blue-500'
            }`}
          >
            <Upload className={`mx-auto h-12 w-12 mb-3 ${isDragging ? 'text-blue-500' : 'text-gray-400'}`} />
            <p className="text-sm text-gray-600 mb-2">
              {isDragging ? 'Drop files here' : 'Drag and drop files here or click to browse'}
            </p>
            <p className="text-xs text-gray-500">
              Supports CSV, XLS, XLSX (max 10MB per file)
            </p>
            <input
              type="file"
              className="hidden"
              accept=".csv,.xls,.xlsx"
              multiple
              onChange={handleFileSelect}
              disabled={isUploading}
            />
          </div>
        </label>
      </div>

      {/* Processing Option */}
      <div className="mb-6">
        <label className="flex items-center">
          <input
            type="checkbox"
            checked={processImmediately}
            onChange={(e) => setProcessImmediately(e.target.checked)}
            disabled={isUploading}
            className="mr-2"
          />
          <span className="text-sm text-gray-700">
            Process files immediately after upload
          </span>
        </label>
      </div>

      {/* Files List */}
      {files.length > 0 && (
        <div className="space-y-3 mb-6">
          <h3 className="text-sm font-medium text-gray-700">
            Files to Upload ({totalFiles})
          </h3>

          {files.map(uploadFile => (
            <div
              key={uploadFile.id}
              className="flex items-center gap-3 p-3 border rounded-lg"
            >
              {/* Status Icon */}
              <div className="flex-shrink-0">
                {uploadFile.status === 'pending' && (
                  <div className="w-5 h-5 rounded-full bg-gray-200" />
                )}
                {uploadFile.status === 'uploading' && (
                  <div className="w-5 h-5 border-2 border-blue-500 border-t-transparent rounded-full animate-spin" />
                )}
                {(uploadFile.status === 'uploaded' || uploadFile.status === 'processing') && (
                  <div className="w-5 h-5 border-2 border-yellow-500 border-t-transparent rounded-full animate-spin" />
                )}
                {uploadFile.status === 'completed' && (
                  <CheckCircle className="w-5 h-5 text-green-500" />
                )}
                {uploadFile.status === 'error' && (
                  <AlertCircle className="w-5 h-5 text-red-500" />
                )}
              </div>

              {/* File Info */}
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium text-gray-900 truncate">
                  {uploadFile.file.name}
                </p>
                <p className="text-xs text-gray-500">
                  {(uploadFile.file.size / 1024).toFixed(2)} KB
                </p>
                {uploadFile.status === 'uploaded' && (
                  <p className="text-xs text-blue-600 mt-1">Uploaded - Processing...</p>
                )}
                {uploadFile.status === 'processing' && (
                  <p className="text-xs text-yellow-600 mt-1">Processing blotter data...</p>
                )}
                {uploadFile.status === 'completed' && (
                  <p className="text-xs text-green-600 mt-1">Processing completed successfully!</p>
                )}
                {uploadFile.error && (
                  <p className="text-xs text-red-600 mt-1">{uploadFile.error}</p>
                )}
              </div>

              {/* Date Input */}
              <div className="flex items-center gap-2">
                <Calendar className="w-4 h-4 text-gray-400" />
                <input
                  type="date"
                  value={uploadFile.date}
                  onChange={(e) => handleDateChange(uploadFile.id, e.target.value)}
                  disabled={uploadFile.status !== 'pending' || isUploading}
                  className="text-sm border rounded px-2 py-1"
                />
              </div>

              {/* Action Buttons */}
              <div className="flex items-center gap-2">
                {uploadFile.status === 'completed' && (
                  <button
                    onClick={() => handleViewBlotter(uploadFile)}
                    className="flex items-center gap-1 px-3 py-1 text-xs font-medium text-blue-700 bg-blue-50 hover:bg-blue-100 rounded-lg transition-colors"
                  >
                    <Eye className="w-3 h-3" />
                    View Report
                  </button>
                )}
                {uploadFile.status === 'pending' && (
                  <button
                    onClick={() => handleRemoveFile(uploadFile.id)}
                    disabled={isUploading}
                    className="flex-shrink-0 text-gray-400 hover:text-red-500"
                  >
                    <X className="w-5 h-5" />
                  </button>
                )}
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Progress Summary */}
      {isUploading && totalFiles > 0 && (
        <div className="mb-6">
          <div className="flex justify-between text-sm mb-2">
            <span className="text-gray-600">Uploading...</span>
            <span className="text-gray-900 font-medium">
              {uploadedCount + errorCount} / {totalFiles}
            </span>
          </div>
          <div className="w-full bg-gray-200 rounded-full h-2">
            <div
              className="bg-blue-500 h-2 rounded-full transition-all duration-300"
              style={{
                width: `${((uploadedCount + errorCount) / totalFiles) * 100}%`
              }}
            />
          </div>
        </div>
      )}

      {/* Processing Status */}
      {!isUploading && processingCount > 0 && (
        <div className="mb-6 p-4 bg-yellow-50 border border-yellow-200 rounded-lg">
          <div className="flex items-center gap-2 text-sm text-yellow-800">
            <div className="w-4 h-4 border-2 border-yellow-600 border-t-transparent rounded-full animate-spin" />
            <span className="font-medium">
              Processing {processingCount} file{processingCount > 1 ? 's' : ''}...
            </span>
          </div>
          <p className="text-xs text-yellow-700 mt-1">
            This may take a few moments. You can safely leave this page.
          </p>
        </div>
      )}

      {/* Action Buttons */}
      <div className="flex justify-end gap-3">
        <button
          onClick={() => setFiles([])}
          disabled={isUploading || files.length === 0}
          className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
        >
          Clear All
        </button>
        <button
          onClick={handleUpload}
          disabled={isUploading || isPreviewing || files.length === 0 || files.every(f => f.status !== 'pending')}
          className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
        >
          {isPreviewing ? (
            <>
              <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
              Checking accounts...
            </>
          ) : isUploading ? (
            <>
              <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
              Uploading...
            </>
          ) : (
            <>
              <Upload className="w-4 h-4" />
              Upload {files.filter(f => f.status === 'pending').length} File(s)
            </>
          )}
        </button>
      </div>

      {/* Success/Error Summary */}
      {!isUploading && totalFiles > 0 && processingCount === 0 && (completedCount > 0 || errorCount > 0) && (
        <div className="mt-4 space-y-3">
          {completedCount > 0 && (
            <div className="p-4 bg-green-50 border border-green-200 rounded-lg">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2 text-green-700">
                  <CheckCircle className="w-5 h-5" />
                  <div>
                    <p className="font-medium">
                      {completedCount} file{completedCount > 1 ? 's' : ''} processed successfully!
                    </p>
                    <p className="text-xs text-green-600 mt-0.5">
                      Click "View Report" next to each file to see the blotter report
                    </p>
                  </div>
                </div>
              </div>
            </div>
          )}
          {errorCount > 0 && (
            <div className="p-4 bg-red-50 border border-red-200 rounded-lg">
              <div className="flex items-center gap-2 text-red-700">
                <AlertCircle className="w-5 h-5" />
                <span className="font-medium">{errorCount} file{errorCount > 1 ? 's' : ''} failed to process</span>
              </div>
            </div>
          )}
        </div>
      )}

      {/* New Account Review Modal */}
      <NewAccountReviewModal
        isOpen={reviewModal.isOpen}
        onClose={() => {
          setReviewModal(prev => ({ ...prev, isOpen: false }));
          // R15 Task B1: explicit cancel discards the retry snapshot.
          setPersistedReview(null);
        }}
        onProceed={handleReviewProceed}
        onSkip={handleReviewSkip}
        newAccounts={reviewModal.newAccounts}
        matchedAccountsNeedingEmployees={reviewModal.matchedAccountsNeedingEmployees}
        filename={reviewModal.filename}
        totalTransactions={reviewModal.totalTransactions}
        totalCommission={reviewModal.totalCommission}
        initialState={persistedReview}
      />
    </div>
  );
};

export default BlotterUpload;
